"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wrench, Flame, ShieldCheck, TriangleAlert as AlertTriangle, Droplets, Zap, PhoneCall, Clock, CircleCheck as CheckCircle2, Settings, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const servicePillars = [{
  id: "installation",
  title: "Installation & Sizing",
  icon: Flame,
  badge: "New & Replacement",
  description: "Properly sized gas and electric tankless systems configured for high demand, optimal flow rate (GPM), and Georgia winter groundwater temperatures.",
  points: ["Precise GPM load calculation for concurrent fixtures", "Gas supply line sizing & dedicated ventilation setups", "Seamless replacement for aging traditional storage tanks"]
}, {
  id: "maintenance",
  title: "Annual Descaling & Flush",
  icon: Settings,
  badge: "Preventive Care",
  description: "Mineral deposits and hard water scale buildup choke heat exchangers. Our scheduled descaling restores maximum thermal efficiency and prevents warranty voids.",
  points: ["Circulating food-grade vinegar flush to clear scale", "Cold water inlet filter & aerator cleaning", "Burner assembly inspection & diagnostic sensor check"]
}, {
  id: "repairs",
  title: "24/7 Emergency Repairs",
  icon: Wrench,
  badge: "Fast Dispatch",
  description: "Rapid diagnostics and factory-grade repairs for all major brands when you lose hot water, encounter error codes, or experience system lockouts.",
  points: ["Immediate error code decoding & sensor replacement", "Ignition failure & gas control valve servicing", "Flow sensor, heating element & thermostat repair"]
}];
const commonIssues = [{
  id: "issue-1",
  question: "Why does my tankless water heater shut off during a shower (Cold Water Sandwich)?",
  answer: "A momentary blast of cold water can occur when residual hot water in pipes is followed by newly heated water. If shutoffs happen mid-shower, it typically indicates a fluctuating flow sensor, low water pressure below the system activation threshold (usually 0.5 GPM), or an overworked unit requiring flow regulation.",
  severity: "Common Flow Issue"
}, {
  id: "issue-2",
  question: "What causes error codes like E1, 11, or 12 (Ignition & Flame Failures)?",
  answer: "Ignition failure codes usually stem from an interrupted gas supply, blocked intake/exhaust vents, dirty flame sensor rods, or inadequate gas line pressure during high demand. Our technicians test gas pressure and clear venting obstructions on arrival.",
  severity: "Immediate Service Needed"
}, {
  id: "issue-3",
  question: "Why is water temperature fluctuating or not reaching the set temperature?",
  answer: "Mineral scale build-up inside the copper or stainless steel heat exchanger insulates the water from heating elements. A thorough descaling flush or recalibration of the mixing valve typically restores consistent water temperatures.",
  severity: "Maintenance Alert"
}, {
  id: "issue-4",
  question: "Why is my tankless system making loud humming, clicking, or knocking sounds?",
  answer: "Clicking is normal during ignition, but loud humming or knocking often points to mineral accumulation around heat exchanger surfaces, fan motor bearing wear, or sudden flow restriction caused by clogged inlet filters.",
  severity: "Mechanical Warning"
}];
const systemBenefits = [{
  icon: Zap,
  title: "Endless Hot Water",
  detail: "Heats water on demand without waiting for a tank to refill."
}, {
  icon: ShieldCheck,
  title: "Up to 20+ Year Lifespan",
  detail: "Nearly double the operating life of traditional tank systems."
}, {
  icon: Droplets,
  title: "Lower Energy Bills",
  detail: "Zero standby heat loss, saving up to 30% on water heating costs."
}, {
  icon: Clock,
  title: "Compact Wall Mount",
  detail: "Reclaims valuable square footage in basements, closets, or utility rooms."
}];
export default function TanklessServices() {
  const [activeTab, setActiveTab] = useState("overview");
  return <section id="tanklesswaterheaterservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase">
            On-Demand Hot Water Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Tankless Water Heater Repair, Maintenance & Installation
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Complete service support for residential and commercial on-demand systems in Jonesboro, GA. From emergency flame sensor fixes to annual descaling.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {systemBenefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <div key={benefit.title} data-index={i} className="bg-card text-card-foreground border border-border rounded-xl p-5 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                <div className="p-2.5 w-fit rounded-lg bg-primary/10 text-primary mb-3">
                  <Icon className="size-5" />
                </div>
                <h3 className="font-default font-semibold text-base mb-1 text-card-foreground">
                  {benefit.title}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {benefit.detail}
                </p>
              </div>;
        })}
        </div>

        {/* Main Services Tabs & Interactive Content */}
        <div className="mb-16">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="grid grid-cols-2 sm:grid-cols-3 max-w-xl w-full p-1 bg-muted">
                <TabsTrigger value="overview" className="text-xs sm:text-sm">
                  Core Services
                </TabsTrigger>
                <TabsTrigger value="diagnostics" className="text-xs sm:text-sm">
                  Diagnostics & Codes
                </TabsTrigger>
                <TabsTrigger value="brands" className="col-span-2 sm:col-span-1 text-xs sm:text-sm">
                  Brands & Support
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab 1: Core Services Grid */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {servicePillars.map((pillar, i) => {
                const PillarIcon = pillar.icon;
                return <Card key={pillar.id} data-index={i} className="border border-border bg-card shadow-xs flex flex-col justify-between">
                      <CardHeader>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="p-3 rounded-lg bg-secondary text-primary">
                            <PillarIcon className="size-6" />
                          </div>
                          <Badge variant="outline" className="text-xs font-medium">
                            {pillar.badge}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-default">
                          {pillar.title}
                        </CardTitle>
                        <CardDescription className="text-sm pt-1 leading-relaxed">
                          {pillar.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2.5 pt-2 border-t border-border/60">
                          {pillar.points.map((pt, ptIdx) => <li key={ptIdx} data-index={ptIdx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span>{pt}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>;
              })}
              </div>
            </TabsContent>

            {/* Tab 2: Diagnostics & Troubleshooting */}
            <TabsContent value="diagnostics">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start bg-card rounded-2xl p-6 sm:p-8 border border-border">
                <div className="lg:col-span-5 space-y-4">
                  <Badge variant="destructive" className="gap-1.5 px-3 py-1">
                    <AlertTriangle className="size-3.5" /> System Warning
                  </Badge>
                  <h3 className="text-2xl font-default font-bold text-card-foreground">
                    Common Tankless Faults & Error Indicators
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Modern tankless units utilize digital microprocessors that shut down operation when detecting overheating, blocked venting, or ignition dropouts. 
                  </p>
                  <div className="rounded-xl bg-muted/60 p-4 border border-border/80">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-sm mb-1">
                      <Clock className="size-4 text-primary" />
                      Jonesboro 24/7 Response Time
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Our vans carry genuine sensors, flow switches, and valves for same-day emergency restores across Clayton County.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button asChild size="lg" className="w-full sm:w-auto">
                      <a href="tel:4708479076" className="inline-flex items-center gap-2">
                        <PhoneCall className="size-4" />
                        Call Plumber Now
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <Accordion type="single" collapsible defaultValue="issue-1" className="w-full">
                    {commonIssues.map((issue, i) => <AccordionItem key={issue.id} value={issue.id} data-index={i} className="border-border">
                        <AccordionTrigger className="text-left font-semibold text-sm sm:text-base py-3.5 hover:no-underline">
                          <span className="pr-2">{issue.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed pt-1">
                          <div className="mb-2">
                            <Badge variant="secondary" className="text-[11px] font-normal py-0">
                              {issue.severity}
                            </Badge>
                          </div>
                          {issue.answer}
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: Brands Serviced */}
            <TabsContent value="brands">
              <div className="bg-card rounded-2xl p-6 sm:p-10 border border-border text-center">
                <h3 className="text-2xl font-default font-bold text-card-foreground mb-3">
                  Certified Service for Leading Tankless Manufacturers
                </h3>
                <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-8">
                  We service, flush, and replace parts on condensing and non-condensing systems from all industry-standard brands using OEM components.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
                  {["Rinnai", "Navien", "Noritz", "Rheem", "Bosch", "Takagi", "A.O. Smith", "State Water Heaters"].map((brand, i) => <div key={brand} data-index={i} className="bg-background border border-border rounded-lg p-4 font-semibold text-sm text-foreground flex items-center justify-center shadow-2xs">
                      {brand}
                    </div>)}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button asChild variant="default">
                    <Link to="/water-heater/">
                      View All Water Heater Services
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/brands/">
                      Explore Supported Brands
                    </Link>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Maintenance CTA Callout Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10">
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-wider text-accent">
                Protect Your Investment
              </span>
              <h3 className="text-2xl sm:text-3xl font-default font-bold mt-1 mb-2 text-primary-foreground">
                Has It Been Over a Year Since Your Last Tankless Flush?
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
                Jonesboro mineral content causes internal scaling that reduces hot water flow and burns out heating elements. Schedule a preventative descaling flush today.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
              <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Schedule Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}